// One model at one scenario (TX-US-0025): /economics/{model_id}/scenario/{default|scenario_id}.
//
// The model's structure is drawn and filled with a working copy of the scenario's values. Every change recalculates on the
// server after a short pause - the engine is the backend's, so a figure here is the figure anyone
// reloading the saved scenario will see. A response that arrives after a newer request is dropped,
// so a fast drag never paints an older position over a newer one.
//
// Saving over puts the copy in place of the open scenario's values, after confirming; saving as
// makes a new scenario from it. Choosing another in the drop-down loads it. Only the person who
// saved a scenario may save over it or delete it; the screen then shows the default. A shared
// baseline may be saved over by anyone and deleted by no one; the Template by no one.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router';

import { calculateCashflow, getEconomicModel, getScenario, listScenarios } from '../api.js';
import { useMe } from '../MeContext.js';
import { notify } from '../ui/notify.jsx';
import CashChart from './CashChart.jsx';
import DealSummary from './DealSummary.jsx';
import DeleteScenarioDialog from './DeleteScenarioDialog.jsx';
import EventTable from './EventTable.jsx';
import {
  addSupplyOrder, blocksLeaving, COMMISSION_WHOLE, commissionTotal, removeSupplyOrder, setIn,
} from './inputs.js';
import InputsPanel from './InputsPanel.jsx';
import Metrics from './Metrics.jsx';
import SaveButton from './SaveButton.jsx';
import SaveOverDialog from './SaveOverDialog.jsx';
import SaveScenarioDialog from './SaveScenarioDialog.jsx';
import UnsavedChangesDialog from './UnsavedChangesDialog.jsx';

const RECALCULATE_AFTER_MS = 200;

// The scenario a model opens on, and the one the screen falls back to.
export const DEFAULT_SCENARIO = 'default';

export const scenarioPath = (modelId, scenarioId) =>
  `/economics/${encodeURIComponent(modelId)}/scenario/${encodeURIComponent(scenarioId)}`;

function useScenarioList(modelId) {
  const [scenarios, setScenarios] = useState([]);
  const refresh = useCallback(
    () => listScenarios(modelId).then(setScenarios).catch(() => setScenarios([])),
    [modelId],
  );
  useEffect(() => { refresh(); }, [refresh]);
  return [scenarios, refresh];
}

function useCalculation(modelId, values) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    if (!values) return undefined;
    const request = ++latest.current;
    const timer = setTimeout(() => {
      calculateCashflow(modelId, values)
        .then((next) => {
          if (request !== latest.current) return;
          setResult(next);
          setError(null);
        })
        .catch((err) => {
          if (request !== latest.current) return;
          setError(err.fields ? Object.values(err.fields).join(' ') : err.message);
        });
    }, RECALCULATE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [modelId, values]);

  return { result, error };
}

// A shared baseline: a saved scenario with no author, which anyone in Economics may save over and
// no one may delete.
const isBaseline = (scenario) => !scenario.built_in && scenario.created_by == null;

function ScenarioPicker({ modelId, scenarioId, scenarios }) {
  const navigate = useNavigate();
  const builtIn = scenarios.filter((s) => s.built_in);
  const baselines = scenarios.filter((s) => isBaseline(s));
  const saved = scenarios.filter((s) => !s.built_in && !isBaseline(s));
  return (
    <label className="econ-picker">
      <span>Scenario</span>
      <select value={scenarioId}
              onChange={(event) => navigate(scenarioPath(modelId, event.target.value))}>
        <optgroup label="Built-in">
          {builtIn.map((s) => <option key={s.scenario_id} value={s.scenario_id}>{s.name}</option>)}
        </optgroup>
        {baselines.length > 0 && (
          <optgroup label="Baselines">
            {baselines.map((s) => (
              <option key={s.scenario_id} value={s.scenario_id}>{s.name}</option>
            ))}
          </optgroup>
        )}
        {saved.length > 0 && (
          <optgroup label="Saved">
            {saved.map((s) => (
              <option key={s.scenario_id} value={s.scenario_id}>
                {s.name} — {s.created_by}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}

export default function ScenarioScreen() {
  const { modelId, scenarioId } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [values, setValues] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const me = useMe();
  const [saving, setSaving] = useState(false);
  const [savingOver, setSavingOver] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scenarios, refreshScenarios] = useScenarioList(modelId);
  const { result, error } = useCalculation(modelId, values);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    Promise.all([getEconomicModel(modelId), getScenario(modelId, scenarioId)])
      .then(([m, scenario]) => {
        if (cancelled) return;
        setModel(m);
        setLoaded(scenario);
        setValues(scenario.values);
        onPurpose.current = false;
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, [modelId, scenarioId]);

  const update = useCallback((path, value) => setValues((prev) => setIn(prev, path, value)), []);
  const changed = loaded && values && JSON.stringify(values) !== JSON.stringify(loaded.values);

  // Unsaved changes are not lost without a warning: leaving the scenario in the application asks
  // first, and closing or reloading the tab gets the browser's own warning. Leaving on purpose -
  // after saving as or deleting - loses nothing, so it does not ask.
  const onPurpose = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => blocksLeaving({
    changed: Boolean(changed),
    onPurpose: onPurpose.current,
    from: currentLocation.pathname,
    to: nextLocation.pathname,
  }));
  useEffect(() => {
    if (!changed) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [changed]);

  const ownScenario = loaded && !loaded.built_in && loaded.created_by === me.email;
  const canSaveOver = ownScenario || (loaded && isBaseline(loaded));
  const commissions = model && values ? commissionTotal(model.structure, values) : COMMISSION_WHOLE;
  const saveBlocked = commissions === COMMISSION_WHOLE ? null
    : `Commission shares add up to ${commissions}%, not 100%`;

  const onDeleted = (scenario) => {
    setDeleting(false);
    notify(`Scenario “${scenario.name}” deleted`);
    refreshScenarios();
    onPurpose.current = true;
    navigate(scenarioPath(modelId, DEFAULT_SCENARIO));
  };

  const onSavedOver = (scenario) => {
    setSavingOver(false);
    setLoaded(scenario);
    setValues(scenario.values);
    notify(`Scenario “${scenario.name}” saved`);
  };

  const onSaved = (scenario) => {
    setSaving(false);
    notify(`Scenario “${scenario.name}” saved`);
    refreshScenarios();
    onPurpose.current = true;
    navigate(scenarioPath(modelId, scenario.scenario_id));
  };

  if (loadError) {
    return (
      <div className="empty">
        <p className="error">{loadError}</p>
        <Link to="/economics">Back to Economics</Link>
      </div>
    );
  }
  if (!model || !values) return <p className="empty">Loading…</p>;

  return (
    <section className="econ">
      <header className="register-header econ-header">
        <div>
          <p className="econ-crumb"><Link to="/economics">Economics</Link></p>
          <h2>{model.name}</h2>
        </div>
        <div className="register-actions">
          <ScenarioPicker modelId={modelId} scenarioId={scenarioId} scenarios={scenarios} />
          <button type="button" disabled={!changed} onClick={() => setValues(loaded.values)}>
            Reset
          </button>
          {ownScenario && (
            <button type="button" onClick={() => setDeleting(true)}>Delete scenario</button>
          )}
          <SaveButton canSaveOver={canSaveOver} changed={changed} blocked={saveBlocked}
                      onSaveOver={() => setSavingOver(true)} onSaveAs={() => setSaving(true)} />
        </div>
      </header>

      <div className="econ-layout">
        <aside className="econ-side">
          <InputsPanel structure={model.structure} values={values} update={update}
                       dated={result?.milestones ?? []}
                       startDates={{
                         starts: result?.starts ?? [],
                         firstPayment: result?.first_buyer_payment ?? null,
                       }}
                       onAddSupplyOrder={() => setValues(addSupplyOrder)}
                       onRemoveSupplyOrder={(i) => setValues(
                         (prev) => removeSupplyOrder(prev, i, result?.starts ?? []),
                       )} />
        </aside>

        <div className="econ-results">
          {error && <p className="error" role="alert">{error}</p>}
          {result ? (
            <>
              <Metrics result={result}
                       beside={(
                         <div className="econ-card">
                           <h3>Deal summary</h3>
                           <DealSummary result={result} />
                         </div>
                       )} />
              <div className="econ-card">
                <h3>Cash position</h3>
                {result.events.length > 0 ? (
                  <CashChart result={result} facilityLimit={values.finance?.facility_limit} />
                ) : (
                  <p className="empty">No cash moves yet: fill the scenario&apos;s dates.</p>
                )}
              </div>
              <div className="econ-card">
                <h3>Cash-flow timeline</h3>
                <EventTable result={result} />
              </div>
            </>
          ) : (
            !error && <p className="empty">Calculating…</p>
          )}
        </div>
      </div>

      {blocker.state === 'blocked' && (
        <UnsavedChangesDialog scenarioName={loaded.name} onStay={() => blocker.reset()}
                              onDiscard={() => blocker.proceed()} />
      )}
      {deleting && (
        <DeleteScenarioDialog modelId={modelId} scenario={loaded}
                              onClose={() => setDeleting(false)} onDeleted={onDeleted} />
      )}
      {savingOver && (
        <SaveOverDialog modelId={modelId} scenario={loaded} values={values}
                        onClose={() => setSavingOver(false)} onSaved={onSavedOver} />
      )}
      {saving && (
        <SaveScenarioDialog modelId={modelId} values={values} onClose={() => setSaving(false)}
                            onSaved={onSaved} />
      )}
    </section>
  );
}
